import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, X, SlidersHorizontal } from "lucide-react";
import { coachlyApi } from "@/lib/api";
import CoachCard from "@/components/coachly/CoachCard";
import MarketplaceNav from "@/components/coachly/MarketplaceNav";
import MarketplaceFilters from "@/components/coachly/MarketplaceFilters";
import { MARKETPLACE } from "@/constants/testIds";

const SORT_OPTIONS = [
  { key: "relevance", label: "Most relevant" },
  { key: "rating", label: "Highest rated" },
  { key: "price_asc", label: "Price: low → high" },
  { key: "price_desc", label: "Price: high → low" },
  { key: "experience", label: "Most experience" },
];

// Every filter that can appear in the URL, in one place, so add/remove-a-filter
// stays centralized and the "clear all" logic is trivial.
const FILTER_KEYS = ["q", "specialty", "mode", "city", "price_min", "price_max", "rating_min", "level", "language", "availability", "sort"];

const _fromParams = (sp) => {
  const out = {};
  FILTER_KEYS.forEach((k) => {
    const v = sp.get(k);
    if (v !== null && v !== "") out[k] = v;
  });
  return out;
};

const _toParams = (state) => {
  const sp = new URLSearchParams();
  Object.entries(state).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "" || v === "any") return;
    sp.set(k, String(v));
  });
  return sp;
};

export default function Marketplace() {
  const [urlParams, setUrlParams] = useSearchParams();
  const [filters, setFilters] = useState(() => _fromParams(urlParams));
  const [coaches, setCoaches] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Debounced fetch — 250ms so typing feels instant without hammering the API.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        // Build params the API understands (skip empty).
        const params = {};
        Object.entries(filters).forEach(([k, v]) => {
          if (v !== "" && v !== undefined && v !== null && v !== "any") params[k] = v;
        });
        const r = await coachlyApi.listCoaches(params);
        if (cancelled) return;
        setCoaches(r.coaches || []);
        setTotal(r.total ?? (r.coaches || []).length);
        if (r.facets) setFacets(r.facets);
      } catch {
        if (!cancelled) { setCoaches([]); setTotal(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [filters]);

  // Reflect state → URL so filters are shareable + survive refresh.
  useEffect(() => {
    const sp = _toParams(filters);
    // Avoid noisy re-renders when nothing meaningful changed.
    if (sp.toString() !== urlParams.toString()) setUrlParams(sp, { replace: true });
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (partial) => setFilters((f) => ({ ...f, ...partial }));
  const clearAll = () => setFilters({});

  const activeChips = useMemo(() => {
    const chips = [];
    if (filters.q) chips.push({ key: "q", label: `“${filters.q}”` });
    if (filters.specialty) chips.push({ key: "specialty", label: filters.specialty });
    if (filters.mode) chips.push({ key: "mode", label: filters.mode === "online" ? "Online" : "In person" });
    if (filters.city) chips.push({ key: "city", label: filters.city });
    if (filters.price_min || filters.price_max) chips.push({
      key: "price",
      label: `$${filters.price_min || facets?.price?.min || 0}–$${filters.price_max || facets?.price?.max || "∞"}`,
    });
    if (filters.rating_min) chips.push({ key: "rating_min", label: `${filters.rating_min}★+` });
    if (filters.level) chips.push({ key: "level", label: filters.level });
    if (filters.language) chips.push({ key: "language", label: filters.language });
    if (filters.availability) chips.push({ key: "availability", label: filters.availability });
    return chips;
  }, [filters, facets]);

  const removeChip = (key) => {
    if (key === "price") return patch({ price_min: "", price_max: "" });
    patch({ [key]: "" });
  };

  return (
    <div data-testid={MARKETPLACE.page} className="bg-white min-h-screen">
      <MarketplaceNav />

      <section className="border-b border-slate-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-10 md:pb-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="eyebrow">§ Marketplace · Beta</div>
            <h1 className="mt-4 font-display font-medium text-[44px] sm:text-[64px] md:text-[80px] leading-[0.95] tracking-[-0.05em] text-slate-900">
              Find a coach who <span className="italic font-light text-[#2563EB]">gets it.</span>
            </h1>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed max-w-[60ch]">
              Every profile is verified. Every review is from a real Coachly member who trained with the coach.
            </p>
          </motion.div>

          {/* Search bar */}
          <div className="mt-10 grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2} />
              <input
                value={filters.q || ""}
                onChange={(e) => patch({ q: e.target.value })}
                data-testid={MARKETPLACE.searchInput}
                placeholder="Search coaches, city, specialty, headline…"
                className="w-full h-12 pl-11 pr-11 rounded-full bg-slate-50 border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25 focus:border-[#2563EB]/40 transition"
              />
              {filters.q && (
                <button
                  onClick={() => patch({ q: "" })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                data-testid={MARKETPLACE.filtersMobileToggle}
                className="md:hidden inline-flex items-center gap-2 h-11 px-4 rounded-full border border-slate-200 text-[13px] font-semibold text-slate-700"
              >
                <SlidersHorizontal className="w-4 h-4" strokeWidth={2.2} />
                Filters
                {activeChips.length > 0 && (
                  <span className="inline-flex items-center h-5 min-w-[20px] px-1.5 rounded-full bg-[#2563EB] text-white text-[10.5px] font-semibold">
                    {activeChips.length}
                  </span>
                )}
              </button>
              <select
                data-testid={MARKETPLACE.sortSelect}
                value={filters.sort || "relevance"}
                onChange={(e) => patch({ sort: e.target.value })}
                className="h-11 rounded-full border border-slate-200 bg-white text-[13px] font-medium text-slate-700 px-4 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
              >
                {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <div className="flex items-center gap-2 text-[13px] text-slate-500">
                <span className="uppercase tracking-[0.2em] text-[11px] font-semibold">Results</span>
                <span data-testid={MARKETPLACE.resultsCount} className="font-semibold text-slate-900">{total}</span>
              </div>
            </div>
          </div>

          {/* Active-filter chips */}
          {activeChips.length > 0 && (
            <div
              data-testid={MARKETPLACE.activeChipsRow}
              className="mt-5 flex flex-wrap items-center gap-2"
            >
              {activeChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => removeChip(chip.key)}
                  data-testid={MARKETPLACE.activeChip(chip.key)}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-700 transition-colors"
                >
                  {chip.label} <X className="w-3 h-3" />
                </button>
              ))}
              <button
                type="button"
                onClick={clearAll}
                data-testid={MARKETPLACE.clearFilters}
                className="text-[12px] text-slate-500 hover:text-slate-900 underline underline-offset-2 ml-1"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex gap-8 md:gap-10">
          {/* Desktop sidebar — hidden while the mobile drawer is open so
              only one `marketplace-filters-panel` exists in the DOM. */}
          {!mobileOpen && (
            <MarketplaceFilters filters={filters} patch={patch} facets={facets} variant="sidebar" />
          )}

          {/* Mobile drawer */}
          {mobileOpen && (
            <MarketplaceFilters
              filters={filters}
              patch={patch}
              facets={facets}
              variant="drawer"
              onClose={() => setMobileOpen(false)}
            />
          )}

          {/* Results grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div data-testid={MARKETPLACE.loading} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="animate-pulse rounded-2xl border border-slate-100 overflow-hidden">
                    <div className="aspect-[4/5] bg-slate-100" />
                    <div className="p-5 space-y-3">
                      <div className="h-4 bg-slate-100 rounded w-2/3" />
                      <div className="h-3 bg-slate-100 rounded w-1/2" />
                      <div className="h-3 bg-slate-100 rounded w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : coaches.length === 0 ? (
              <div data-testid={MARKETPLACE.emptyState} className="text-center py-24">
                <div className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Nothing matches</div>
                <h3 className="mt-4 font-display text-3xl tracking-tight text-slate-900">
                  No coaches for that combination.
                </h3>
                <p className="mt-2 text-slate-500">Try relaxing a filter or expanding your price range.</p>
                <button
                  onClick={clearAll}
                  className="mt-6 h-11 px-5 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13px] font-semibold transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {coaches.map((coach, i) => (
                  <CoachCard key={coach.id} coach={coach} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
